import React, { useEffect, useState } from "react";
import { CircularProgress, Box } from "@mui/material";
import UnifiedWalletHeader from "./WalletCards";
import ExpiringBonusChips from "./ExpiringBonusChips";
import WalletHistoryTable from "./WalletHistoryTable";
import PurchaseDialog from "./PurchaseDialog";
import RedeemDialog from "./RedeemDialog";
import TalentEarningsTable from "../TalentEarningTable";
import { useAuth } from "../../../hooks";
import { useGetTalentEarnings } from "../../../services/api/user.api";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};

// Interfaces
interface ExpiringBatch {
  amount: number;
  expires_at: string;
}

interface WalletHistory {
  date: string;
  amount: number;
  chip_type: "PChip" | "RChip" | "BChip";
  action: string;
}

interface AccountBalance {
  id: string | number;
  type: string;
  name: string;
  profileImage: string;
  balance: number;
  status: string;
}

const WalletDashboard: React.FC = () => {
  const { userInfo } = useAuth();
  const { data: talentEarnings } = useGetTalentEarnings({
    key: ["talent-earnings"],
  });

  const [loading, setLoading] = useState(true);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);

  const [history, setHistory] = useState<WalletHistory[]>([]);
  const [filter, setFilter] = useState<string>("All");
  const [actionFilter, setActionFilter] = useState<string>("All");
  const [dateFilter, setDateFilter] = useState<string>("All");

  const [purchaseAmount, setPurchaseAmount] = useState<number>(100);
  const [redeemAmount, setRedeemAmount] = useState<number>(0);
  const [bankInfo, setBankInfo] = useState({
    bank_name: "",
    account_number: "",
    account_holder: "",
  });

  // Pagination & Sorting
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState<keyof WalletHistory>("date");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  // ✅ Chips Data
  const purchasedChips = Number(
    userInfo?.wallet_purchase?.available_chips ?? 0
  );
  const earnedChips = Number(userInfo?.wallet_earning?.available_chips ?? 0);
  const bonusChips = 300; // Dummy until backend supports

  // ✅ Expiring Bonus Chips (Dummy)
  const expiringBChip: ExpiringBatch[] = [
    { amount: 100, expires_at: "2025-12-31" },
    { amount: 200, expires_at: "2026-01-15" },
  ];

  // ✅ History (Dummy)
  useEffect(() => {
    setTimeout(() => {
      const dummyHistory: WalletHistory[] = [
        {
          date: "2025-07-01",
          amount: 200,
          chip_type: "PChip",
          action: "purchase",
        },
        {
          date: "2025-07-05",
          amount: 150,
          chip_type: "BChip",
          action: "promotion",
        },
        {
          date: "2025-07-10",
          amount: 100,
          chip_type: "RChip",
          action: "booking_earn",
        },
        {
          date: "2025-07-15",
          amount: 300,
          chip_type: "RChip",
          action: "redeem",
        },
      ];
      setHistory(dummyHistory);
      setLoading(false);
    }, 1000);
  }, []);

  // ✅ Talent & GTP Account Balances
  const [accountBalances, setAccountBalances] = useState<AccountBalance[]>(
    () => {
      return (
        userInfo?.talent?.flatMap((talent: any) => {
          const talentAccount: AccountBalance = {
            id: talent.id,
            type: "Talent",
            name: talent.username,
            balance: Number(talent.wallet_balance ?? 0),
            profileImage: talent.profile_image,
            status: "Active",
          };

          const gtpAccounts =
            talent?.gtps?.map((gtp: any) => ({
              id: gtp.id,
              type: "Group",
              name: gtp.name,
              balance: 0,
              profileImage: gtp.profile_image,
              status: "Active",
            })) || [];

          return [talentAccount, ...gtpAccounts];
        }) || []
      );
    }
  );

  // ✅ Update balances dynamically from API
  useEffect(() => {
    const earningsArray = talentEarnings?.data;
    if (!Array.isArray(earningsArray)) return;

    setAccountBalances((prev) =>
      prev.map((acc) => {
        if (acc.type === "Talent") {
          const match = earningsArray.find((e: any) => e.talent?.id === acc.id);
          if (match) return { ...acc, balance: match.chips };
        }
        if (acc.type === "Group") {
          const match = earningsArray.find((e: any) => e.gtp?.id === acc.id);
          if (match) return { ...acc, balance: match.chips };
        }
        return acc;
      })
    );
  }, [talentEarnings?.data]);

  // ✅ Total USD Value
  const totalValueUSD = (purchasedChips + earnedChips) * 0.1;

  // Helpers
  const calculateProgress = (expiryDate: string) => {
    const today = new Date().getTime();
    const expiry = new Date(expiryDate).getTime();
    const daysRemaining = (expiry - today) / (1000 * 3600 * 24);
    const percent = Math.max(0, Math.min(100, (daysRemaining / 180) * 100));
    return percent;
  };

  const handleSort = (property: keyof WalletHistory) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedHistory = [...history].sort((a, b) => {
    if (orderBy === "amount") {
      return order === "asc" ? a.amount - b.amount : b.amount - a.amount;
    } else {
      return order === "asc"
        ? new Date(a.date).getTime() - new Date(b.date).getTime()
        : new Date(b.date).getTime() - new Date(a.date).getTime();
    }
  });

  const paginatedHistory = sortedHistory
    .filter((h) => filter === "All" || h.chip_type === filter)
    .filter((h) => actionFilter === "All" || h.action === actionFilter)
    .filter((h) => {
      if (dateFilter === "All") return true;
      const now = new Date();
      const itemDate = new Date(h.date);
      if (dateFilter === "Last 7 Days") {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(now.getDate() - 7);
        return itemDate >= sevenDaysAgo;
      }
      if (dateFilter === "This Month") {
        return (
          itemDate.getMonth() === now.getMonth() &&
          itemDate.getFullYear() === now.getFullYear()
        );
      }
      return true;
    })
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // ✅ Dummy actions
  const handlePurchase = () => {
    alert(`Stripe simulation for $${purchaseAmount}`);
    setPurchaseOpen(false);
  };

  const handleRedeem = () => {
    if (earnedChips < redeemAmount) {
      alert("Insufficient balance");
      return;
    }
    alert(`Redeemed ${redeemAmount} chips`);
    setRedeemOpen(false);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        mt={6}
        sx={{ color: COLORS.text }}
      >
        <CircularProgress sx={{ color: COLORS.red }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: COLORS.bg,
        color: COLORS.text,
        minHeight: "100vh",
        py: 4,
      }}
    >
      {/* Wallet Cards */}
      <UnifiedWalletHeader
        totalValueUSD={totalValueUSD}
        onPurchaseClick={() => setPurchaseOpen(true)}
        onRedeemClick={() => setRedeemOpen(true)}
        purchasedChips={purchasedChips}
        earnedChips={earnedChips}
        expiringBonus={100}
        bonusChips={bonusChips}
      />

      {/* Expiring Bonus Chips */}
      <ExpiringBonusChips
        expiringBChip={expiringBChip}
        calculateProgress={calculateProgress}
      />

      {/* Talent Earnings */}
      <TalentEarningsTable accountBalances={accountBalances} />

      {/* Wallet History */}
      <WalletHistoryTable
        history={history}
        paginatedHistory={paginatedHistory}
        filter={filter}
        setFilter={setFilter}
        actionFilter={actionFilter}
        setActionFilter={setActionFilter}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        orderBy={orderBy}
        order={order}
        handleSort={handleSort}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
      />

      {/* Purchase & Redeem Dialogs */}
      <PurchaseDialog
        open={purchaseOpen}
        onClose={() => setPurchaseOpen(false)}
        purchaseAmount={purchaseAmount}
        setPurchaseAmount={setPurchaseAmount}
        onConfirm={handlePurchase}
      />
      <RedeemDialog
        open={redeemOpen}
        onClose={() => setRedeemOpen(false)}
        redeemAmount={redeemAmount}
        setRedeemAmount={setRedeemAmount}
        bankInfo={bankInfo}
        setBankInfo={setBankInfo}
        onConfirm={handleRedeem}
      />
    </Box>
  );
};

export default WalletDashboard;
